import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSessionState } from "@/hooks/useSessionState";
import { useToast } from "@/hooks/use-toast";
import {
  fetchOnboardingFlows,
  saveOnboardingFlow,
  deleteOnboardingFlow,
} from "@/api/onboarding";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Loader2,
  FileText,
} from "lucide-react";
import type { OnboardingFlow } from "@/types/onboarding";

interface FlowFilters {
  search: string;
  isActive: string; // "all" | "true" | "false"
}

export default function OnboardingFlowsList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useSessionState<FlowFilters>(
    "onboarding-flows-filters",
    { search: "", isActive: "all" }
  );
  const [deletingFlow, setDeletingFlow] = useState<OnboardingFlow | null>(null);

  const {
    data: flows = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["onboarding-flows"],
    queryFn: fetchOnboardingFlows,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOnboardingFlow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding-flows"] });
      toast({ title: "Flow deleted" });
      setDeletingFlow(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleToggleActive = async (flow: OnboardingFlow) => {
    try {
      // Optimistic update
      queryClient.setQueryData(
        ["onboarding-flows"],
        (old: OnboardingFlow[] | undefined) =>
          (old || []).map((f) =>
            f.id === flow.id ? { ...f, isActive: !f.isActive } : f
          )
      );
      await saveOnboardingFlow(
        {
          name: flow.name,
          slug: flow.slug,
          description: flow.description || undefined,
          isActive: !flow.isActive,
          isSkippable: flow.isSkippable,
        },
        flow.id
      );
      queryClient.invalidateQueries({ queryKey: ["onboarding-flows"] });
    } catch (error: any) {
      queryClient.invalidateQueries({ queryKey: ["onboarding-flows"] });
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  // Filter flows
  const filteredFlows = flows.filter((flow) => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (
        !flow.name.toLowerCase().includes(search) &&
        !flow.slug.toLowerCase().includes(search)
      ) {
        return false;
      }
    }
    if (filters.isActive === "true" && !flow.isActive) return false;
    if (filters.isActive === "false" && flow.isActive) return false;
    return true;
  });

  const addFlowButton = (
    <Button onClick={() => navigate("/admin/onboarding/new")}>
      <Plus className="w-4 h-4 mr-2" />
      New Flow
    </Button>
  );

  if (isError) {
    return (
      <AdminLayout title="Onboarding Flows" actionButton={addFlowButton}>
        <Card>
          <CardContent className="py-12 text-center text-red-500 dark:text-red-400">
            Failed to load onboarding flows.
          </CardContent>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Onboarding Flows" actionButton={addFlowButton}>
      <div className="space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or slug..."
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  className="pl-10"
                />
              </div>
              <Select
                value={filters.isActive}
                onValueChange={(val) =>
                  setFilters({ ...filters, isActive: val })
                }
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="true">Active</SelectItem>
                  <SelectItem value="false">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400 dark:text-gray-500" />
          </div>
        ) : filteredFlows.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p>
                {flows.length === 0
                  ? "No onboarding flows yet. Create your first flow to get started."
                  : "No flows match your filters."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Pages</TableHead>
                  <TableHead>Skippable</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFlows.map((flow) => (
                  <TableRow
                    key={flow.id}
                    className="cursor-pointer"
                    onClick={() =>
                      navigate(`/admin/onboarding/${flow.id}/edit`)
                    }
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">{flow.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{flow.slug}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {flow.pageCount ?? 0} pages
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={flow.isSkippable ? "outline" : "secondary"}>
                        {flow.isSkippable ? "Yes" : "No"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={flow.isActive}
                        onCheckedChange={() => handleToggleActive(flow)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/admin/onboarding/${flow.id}/edit`
                              );
                            }}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingFlow(flow);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deletingFlow}
        onOpenChange={() => setDeletingFlow(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Flow</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deletingFlow?.name}"? This will
              also delete all its pages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deletingFlow) deleteMutation.mutate(deletingFlow.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
